import './App.css';

import React from 'react';

import InfoScreenLayout from './components/InfoScreenLayout';
import useDisplayProfile from './hooks/useDisplayProfile';
import useInfoScreenData from './hooks/useInfoScreenData';

const DevelopmentScenarioPreview =
  process.env.NODE_ENV === 'development'
    ? React.lazy(() => import('./dev/InfoScreenScenarioPreview'))
    : null;

function messageKey(message) {
  if (!message) return 'empty';
  if (message.type === 'message') {
    return `message:${message.crfd1_content || ''}:${message.crfd1_duration || ''}:${message.crfd1_priority || ''}`;
  }
  if (message.type === 'birthday') {
    return `birthday:${message.fullname || ''}:${message.mobilephone || ''}:${message.shiur || ''}`;
  }
  if (message.type === 'chair-lifting') {
    const locations = (message.chairLocations || []).map((location) => location.value).join(',');
    return `chair-lifting:${message.generatedDateKey || ''}:${locations}`;
  }
  if (message.type === 'shabbat-planning') {
    return `shabbat-planning:${message.eventId}:${message.crfd1_title}:${message.shabbatKind}:${message.registrationDeadline}:${message.attendanceText}`;
  }
  if (message.type === 'zl') {
    return `zl:${message.crfd1_name || ''}:${message.crfd1_day || ''}:${message.crfd1_month || ''}:${message.crfd1_year || ''}`;
  }
  return message.type || 'empty';
}

export function useVisibleMessages(messages) {
  const [clock, setClock] = React.useState(() => Date.now());
  const visibleMessages = React.useMemo(() => {
    const now = Math.max(clock, Date.now());
    return messages.filter(message => !Number.isFinite(message.expiresAt) || message.expiresAt > now);
  }, [clock, messages]);

  React.useEffect(() => {
    const expiresAt = Math.min(...visibleMessages.map(message => message.expiresAt).filter(Number.isFinite));
    if (!Number.isFinite(expiresAt)) return undefined;
    const timer = window.setTimeout(() => setClock(Date.now()), Math.max(1, expiresAt - Date.now()));
    const resume = () => { if (!document.hidden) setClock(Date.now()); };
    document.addEventListener('visibilitychange', resume);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [visibleMessages]);
  return visibleMessages;
}

function LiveInfoScreen() {
  const displayProfile = useDisplayProfile();
  const { data, isInitialLoading } = useInfoScreenData({
    privacyProfile: displayProfile.profile,
    enabled: displayProfile.resolved
  });
  const [messageIndex, setMessageIndex] = React.useState(null);
  const messages = useVisibleMessages(data.messages);
  const previousMessagesRef = React.useRef([]);
  const messagesRef = React.useRef(messages);
  messagesRef.current = messages;
  const messagesSignature = React.useMemo(
    () => messages.map(messageKey).join('|'),
    [messages]
  );

  React.useEffect(() => {
    if (!messages.length) {
      setMessageIndex(null);
      previousMessagesRef.current = [];
      return;
    }

    setMessageIndex((prevIndex) => {
      const previousMessage = prevIndex === null ? null : previousMessagesRef.current[prevIndex];
      const previousKey = messageKey(previousMessage);
      const preservedIndex = messages.findIndex((message) => messageKey(message) === previousKey);
      if (preservedIndex >= 0) {
        return preservedIndex;
      }
      return 0;
    });
    previousMessagesRef.current = messages;
  }, [messages, messagesSignature]);

  React.useEffect(() => {
    const messages = messagesRef.current;
    if (!messages.length || messageIndex === null) {
      return;
    }

    const currentMessage = messages[messageIndex];
    const timeoutMs =
      messages.length > 1 ? (currentMessage?.crfd1_duration || 6) * 1000 : 60000;

    const timer = setTimeout(() => {
      setMessageIndex((prevIndex) => {
        if (prevIndex === null) {
          return 0;
        }

        return (prevIndex + 1) % messages.length;
      });
    }, timeoutMs);

    return () => {
      clearTimeout(timer);
    };
  }, [messageIndex, messagesSignature]);

  const currentMessage =
    messageIndex === null ? { type: 'empty' } : messages[messageIndex] || { type: 'empty' };

  return (
    <InfoScreenLayout
      data={data}
      currentMessage={currentMessage}
      isInitialLoading={isInitialLoading}
    />
  );
}

function App() {
  const previewParams = DevelopmentScenarioPreview
    ? new URLSearchParams(window.location.search)
    : null;
  const scenarioName = previewParams?.get('scenario') || '';

  if (DevelopmentScenarioPreview && scenarioName) {
    return (
      <React.Suspense fallback={<div className="App"><span>טוען תרחיש...</span></div>}>
        <DevelopmentScenarioPreview
          name={scenarioName}
          showsMessagesPanel={previewParams.get('messages') !== 'hidden'}
        />
      </React.Suspense>
    );
  }

  return <LiveInfoScreen />;
}

export default App;
